import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bot, Users, PartyPopper, Briefcase, PiggyBank, Map, UtensilsCrossed, Home,
  Smartphone, Landmark, Luggage, Gift, Plane, GraduationCap, Sparkles,
  Check, Megaphone, LogOut, ArrowRight
} from "lucide-react";
import api, { trackActivity } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/ui-kit";
import { toast } from "sonner";

const ICONS = { Bot, Users, PartyPopper, Briefcase, PiggyBank, Map, UtensilsCrossed, Home, Smartphone, Landmark, Luggage, Gift, Plane, GraduationCap, Sparkles };

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [cards, setCards] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [packing, setPacking] = useState(null);

  useEffect(() => {
    trackActivity("page_visit", "Visited student portal", "portal");
    (async () => {
      try {
        const [j, c, a, p] = await Promise.all([api.get("/journey"), api.get("/cards"), api.get("/announcements"), api.get("/packing")]);
        setJourney(j.data);
        setCards(c.data);
        setAnnouncements(a.data);
        setPacking(p.data);
      } catch {
        toast.error("Could not load your portal. Please refresh.");
      }
    })();
  }, []);

  const toggle = async (task) => {
    const next = !task.completed;
    setJourney((j) => ({
      ...j,
      tasks: j.tasks.map((t) => (t.task_id === task.task_id ? { ...t, completed: next } : t)),
      completed_count: j.completed_count + (next ? 1 : -1),
    }));
    try {
      await api.post("/journey/toggle", { task_id: task.task_id, completed: next });
    } catch {
      toast.error("Could not save. Try again.");
    }
  };

  const openCard = (card) => {
    trackActivity("feature_click", `Opened ${card.title}`, "portal", { card: card.title });
    if (card.link === "/portal/ai") navigate("/portal/ai");
    else toast.info(`${card.title} is coming soon to your portal.`);
  };

  const pct = journey && journey.total ? Math.round((journey.completed_count / journey.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#060D1F]" data-testid="student-portal">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-2 text-sm text-slate-400 transition-colors duration-300 hover:text-[#D4AF37]"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">You're not alone anymore</p>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight" data-testid="welcome-heading">
            Welcome back, {user?.first_name || "friend"}
          </h1>
          {user?.uk_city && (
            <p className="mt-3 text-slate-400">Your {user.uk_city} journey is underway{user.arrival_month ? ` - arriving ${user.arrival_month}` : ""}.</p>
          )}
          {user?.email && !user?.email_verified && (
            <button data-testid="verify-banner" onClick={() => navigate("/verify-email")}
              className="mt-5 text-sm rounded-full border border-[#D4AF37]/40 text-[#D4AF37] px-5 py-2 transition-colors duration-300 hover:bg-[#D4AF37]/10">
              Verify your email to unlock everything
            </button>
          )}
        </motion.div>

        {announcements.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-10 rounded-3xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.06] px-7 py-5 flex items-start gap-4" data-testid="announcements-banner">
            <Megaphone className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
            <div>
              <p className="text-white font-medium">{announcements[0].title}</p>
              {announcements[0].body && <p className="text-sm text-slate-400 mt-1">{announcements[0].body}</p>}
            </div>
          </motion.div>
        )}

        {/* PACKING CHECKLIST */}
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
          className="mt-10 rounded-3xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] p-8 sm:p-10" data-testid="packing-card">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center">
                  <Luggage className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.8} />
                </div>
                <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">Pre-Flight Packing Checklist</p>
              </div>
              <h2 className="mt-4 text-2xl sm:text-3xl text-white tracking-tight">Everything you need to pack before moving to the UK.</h2>
              <p className="mt-3 text-sm text-slate-400" data-testid="packing-progress-text">
                {packing ? `${packing.packed_ids.length} of ${packing.total} items packed · ${Math.round((packing.packed_ids.length / packing.total) * 100)}% packed` : "0% packed"}
              </p>
              <div className="mt-4 h-2 max-w-md rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-[#D4AF37] rounded-full transition-all duration-500" style={{ width: `${packing ? Math.round((packing.packed_ids.length / packing.total) * 100) : 0}%` }} />
              </div>
            </div>
            <button data-testid="open-packing-checklist" onClick={() => navigate("/portal/packing")}
              className="rounded-full bg-[#D4AF37] text-[#0A1633] font-semibold px-7 py-3.5 text-sm sm:text-base transition-all duration-300 hover:bg-[#e5c558] hover:-translate-y-0.5 shrink-0">
              Open My Packing Checklist
            </button>
          </div>
        </motion.section>

        {/* JOURNEY TRACKER */}
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-12 rounded-3xl border border-white/10 bg-white/[0.03] p-8 sm:p-10" data-testid="journey-tracker">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <h2 className="text-2xl sm:text-3xl text-white tracking-tight">Your UK Journey</h2>
              <p className="mt-2 text-sm text-slate-400">Tick things off as you go. Every step counts.</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-display text-[#D4AF37]" data-testid="journey-progress-text">{journey?.completed_count ?? 0}/{journey?.total ?? 0}</p>
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500">completed</p>
            </div>
          </div>
          <div className="mt-6 h-2 rounded-full bg-white/5 overflow-hidden">
            <motion.div className="h-full bg-[#D4AF37] rounded-full" animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} />
          </div>
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {journey?.tasks.map((t) => (
              <button
                key={t.task_id}
                data-testid={`journey-task-${t.task_id}`}
                onClick={() => toggle(t)}
                className={`flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${
                  t.completed ? "border-[#D4AF37]/50 bg-[#D4AF37]/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40 hover:-translate-y-0.5"
                }`}
              >
                <span className={`w-6 h-6 rounded-lg border flex items-center justify-center shrink-0 transition-colors duration-300 ${
                  t.completed ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/25"
                }`}>
                  {t.completed && <Check className="w-4 h-4 text-[#0A1633]" strokeWidth={3} />}
                </span>
                <span className={t.completed ? "text-white line-through opacity-70" : "text-slate-200"}>{t.title}</span>
              </button>
            ))}
          </div>
        </motion.section>

        {/* FEATURE CARDS */}
        <section className="mt-14" data-testid="feature-cards-section">
          <h2 className="text-2xl sm:text-3xl text-white tracking-tight">Everything you need, in one place</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {cards.map((c, i) => {
              const Icon = ICONS[c.icon] || Sparkles;
              return (
                <motion.button
                  key={c.card_id}
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.03 * i }}
                  data-testid={`feature-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => openCard(c)}
                  className="text-left rounded-3xl border border-white/10 bg-white/[0.03] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40 hover:bg-white/[0.05]"
                >
                  <Icon className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.7} />
                  <h3 className="mt-5 text-lg text-white font-display flex items-center gap-2">
                    {c.title}
                    {c.title === "Castles AI" && <ArrowRight className="w-4 h-4 text-[#D4AF37]" />}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.description}</p>
                </motion.button>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}
